import { useState, useEffect } from "react";
import type { AxiosInstance } from "axios";
import type { SdwanHubDetailsResponse } from "../sdwan.types";
import { fetchSdwanHubDetails } from "../requests/request";

/**
 * Load SD-WAN hub detail (Nivel 2):
 * GET /kontroledge/sdwan/:id?tenantId=
 * FortiManager data is composed live server-side — expect a few seconds on
 * first load (one multi-target call covers every branch).
 */
export function useSdwanHubDetails(
    sdwanId: string | null,
    api: AxiosInstance,
    refresh: number,
    tenantId: number | null
) {
    const [raw, setRaw] = useState<SdwanHubDetailsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!sdwanId || tenantId == null) {
            setRaw(null);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const response = await fetchSdwanHubDetails(
                    api,
                    tenantId,
                    sdwanId
                );
                if (cancelled) return;
                const body = response.data;
                if (!body?.success || !body.data) {
                    throw new Error(
                        body?.message ?? "Failed to load SD-WAN hub"
                    );
                }
                setRaw(body.data);
                setError(null);
            } catch (e) {
                console.error("Error in useSdwanHubDetails:", e);
                if (!cancelled) {
                    const status = (e as { response?: { status?: number } })
                        ?.response?.status;
                    setRaw(null);
                    // 404 → service not found (empty state); other failures
                    // (500 / FMG down / network) → error state with retry.
                    setError(
                        status === 404
                            ? null
                            : "We couldn't load this SD-WAN service. Please try again."
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [sdwanId, tenantId, api, refresh]);

    return { raw, loading, error };
}
